import { Group, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { WidgetPropsComponentProps } from '../getWidgetPropsComponent';
import { isKpiThresholds, sanitizeKpiHref } from './kpiData';

/**
 * TV1-1930 (S15) — builder props for the KPI widget.
 *
 * Shared VERBATIM by the web and mobile builders (both `WebWidgetProps` and
 * `MobileWidgetProps` import this one component). The KPI renders identically on
 * both devices, so a second copy would only be a second place for the
 * `goodDirection` default to drift.
 *
 * Note what is NOT here:
 *  - **No period picker.** The comparison period comes from the shared Date Range
 *    section (its `compare` toggle) — building a second period picker here would
 *    let the two disagree.
 *
 * PREFACTOR-grammar — the old note here read *"No target/threshold. Not in the AC;
 * not built."* It is built now: `thresholds` colours the VALUE against a band
 * (decoupled from `hasCompare`, which is what a snapshot metric like seller
 * reputation needs — it has no history to compare against), `valueMode: 'text'`
 * lets a KPI display a non-numeric member, and `href` makes an operational KPI a
 * link. All three ride on `widgetComponentProps`, so none of them cost a domain
 * schema change.
 */

/** Kept in sync with `KpiGoodDirection` in `kpiData.ts`. */
const GOOD_DIRECTIONS = [ 'neutral', 'up', 'down' ] as const;

export const KpiPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};

  const handleUpdate = ( key: string, value: unknown ) =>
    updateConfig( 'widgetComponentProps', { ...widgetComponentProps, [key]: value });

  const format = widgetComponentProps.format ?? 'number';
  const thresholds = isKpiThresholds( widgetComponentProps.thresholds )
    ? widgetComponentProps.thresholds
    : undefined;

  // A non-empty href that the renderer's allow-list will reject. Empty = no link at
  // all, which is the default and not an error.
  const hrefError = Boolean( widgetComponentProps.href ) && sanitizeKpiHref( widgetComponentProps.href ) === null;

  return (
    <Stack gap="md">
      <Select
        label={t( 'widget_kpi_format_label', 'Format' )}
        description={t( 'widget_kpi_format_description', 'How the number is displayed' )}
        data={[
          { value: 'number', label: t( 'widget_kpi_format_number', 'Number' ) },
          { value: 'currency', label: t( 'widget_kpi_format_currency', 'Currency' ) },
          { value: 'percent', label: t( 'widget_kpi_format_percent', 'Percent' ) }
        ]}
        value={format}
        onChange={( value ) => handleUpdate( 'format', value )}
        clearable={false}
        radius="xs"
        checkIconPosition="right"
      />

      {format === 'currency' && (
        <TextInput
          label={t( 'widget_kpi_currency_label', 'Currency code' )}
          // No default and no guess: an unset currency renders a PLAIN number.
          // Inventing "ARS" would mislabel every figure in another currency, and a
          // confidently-wrong currency symbol is worse than none.
          description={t(
            'widget_kpi_currency_description',
            'ISO-4217 code, e.g. ARS or USD. Left empty, the value renders as a plain number.'
          )}
          placeholder="ARS"
          value={widgetComponentProps.currency ?? ''}
          onChange={( event ) => handleUpdate( 'currency', event.currentTarget.value.trim().toUpperCase() || undefined )}
          radius="xs"
        />
      )}

      <NumberInput
        label={t( 'widget_kpi_decimals_label', 'Decimal places' )}
        description={t( 'widget_kpi_decimals_description', 'Leave empty for automatic (up to 2)' )}
        min={0}
        max={6}
        value={widgetComponentProps.decimals ?? ''}
        onChange={( value ) =>
          handleUpdate( 'decimals', typeof value === 'number' && Number.isFinite( value ) ? value : undefined )
        }
        radius="xs"
      />

      <Select
        label={t( 'widget_kpi_good_direction_label', 'Which direction is good?' )}
        // The rationale, surfaced to the person actually choosing: nothing in the
        // system knows whether a rise in THIS measure is good news. For a return
        // rate or a cancellation rate, up is bad. So the default is "no colour",
        // and colour is something you opt into having thought about it.
        description={t(
          'widget_kpi_good_direction_description',
          'Colours the delta. Defaults to no colour — for metrics like return rate, a rise is bad, and a confidently green tile would be misleading.'
        )}
        data={[
          { value: 'neutral', label: t( 'widget_kpi_good_direction_neutral', 'No colour (default)' ) },
          { value: 'up', label: t( 'widget_kpi_good_direction_up', 'Up is good (e.g. revenue)' ) },
          { value: 'down', label: t( 'widget_kpi_good_direction_down', 'Down is good (e.g. return rate)' ) }
        ]}
        value={GOOD_DIRECTIONS.includes( widgetComponentProps.goodDirection ) ? widgetComponentProps.goodDirection : 'neutral'}
        onChange={( value ) => handleUpdate( 'goodDirection', value )}
        clearable={false}
        radius="xs"
        checkIconPosition="right"
      />

      <TextInput
        label={t( 'widget_kpi_caption_label', 'Caption' )}
        description={t( 'widget_kpi_caption_description', 'Optional label under the number' )}
        value={widgetComponentProps.caption ?? ''}
        onChange={( event ) => handleUpdate( 'caption', event.currentTarget.value || undefined )}
        radius="xs"
      />

      {/*
        * PREFACTOR-grammar — the three props the docblock above used to say were
        * "not built". They ride on `widgetComponentProps`
        * (`additionalProperties: true`), so none of them cost a schema change.
        */}

      <Select
        label={t( 'widget_kpi_value_mode_label', 'Value type' )}
        description={t(
          'widget_kpi_value_mode_description',
          'Text is for members that are not numbers — a reputation level like "5_green" would otherwise render as "—".'
        )}
        data={[
          { value: 'number', label: t( 'widget_kpi_value_mode_number', 'Number (default)' ) },
          { value: 'text', label: t( 'widget_kpi_value_mode_text', 'Text' ) }
        ]}
        value={widgetComponentProps.valueMode === 'text' ? 'text' : 'number'}
        onChange={( value ) => handleUpdate( 'valueMode', value === 'text' ? 'text' : undefined )}
        clearable={false}
        radius="xs"
        checkIconPosition="right"
      />

      <Group grow align="flex-start">
        <NumberInput
          label={t( 'widget_kpi_threshold_value_label', 'Threshold' )}
          // TWO bands, not three, and the description says so. These are pass/fail
          // BOUNDARIES (MercadoLibre publishes "claims < 3%", not a middle band);
          // a green/amber/red scale would need a second boundary nobody has defined.
          description={t(
            'widget_kpi_threshold_value_description',
            'Colours the value itself: meets the bar, or does not. Exactly on the threshold counts as meeting it. Leave empty for no colour.'
          )}
          value={thresholds?.value ?? ''}
          onChange={( value ) =>
            handleUpdate(
              'thresholds',
              typeof value === 'number' && Number.isFinite( value )
                ? { value, direction: thresholds?.direction ?? 'below' }
                : undefined
            )
          }
          radius="xs"
        />

        <Select
          label={t( 'widget_kpi_threshold_direction_label', 'Good side' )}
          data={[
            { value: 'below', label: t( 'widget_kpi_threshold_direction_below', 'At or below is good' ) },
            { value: 'above', label: t( 'widget_kpi_threshold_direction_above', 'At or above is good' ) }
          ]}
          value={thresholds?.direction ?? 'below'}
          disabled={!thresholds}
          onChange={( value ) =>
            thresholds && handleUpdate( 'thresholds', { ...thresholds, direction: value === 'above' ? 'above' : 'below' })
          }
          clearable={false}
          radius="xs"
          checkIconPosition="right"
        />
      </Group>

      <TextInput
        label={t( 'widget_kpi_href_label', 'Link target' )}
        description={t(
          'widget_kpi_href_description',
          'Makes the number clickable. An operational KPI is a call to action — "12 orders to fulfil" should take you to the 12 orders. e.g. /orders/list'
        )}
        placeholder="/orders/list"
        value={widgetComponentProps.href ?? ''}
        onChange={( event ) => handleUpdate( 'href', event.currentTarget.value.trim() || undefined )}
        // TV1-1934 — the SAME allow-list the renderer enforces, surfaced at authoring
        // time. `KpiWidget` is the authority (this form is not the only way a
        // definition can be written — an imported or API-authored layout never passes
        // through here), but a rejected href renders as plain text with no anchor, and
        // an author who typed one deserves to be told why their link does nothing
        // rather than discovering it on a dashboard other people are also reading.
        error={
          hrefError
            ? t(
              'widget_kpi_href_error',
              'Only in-app paths (/orders/list) and http(s):// links are allowed. This will render as plain text, not a link.'
            )
            : undefined
        }
        radius="xs"
      />
    </Stack>
  );
};
